import { getMeals } from "@/packages/dashboard/api/get-meals";
import type { UserTargets } from "@/packages/dashboard/api/user-targets";
import { useUserTargets } from "@/packages/dashboard/UserTargets/UserTargetsProvider";
import { formatIsoDate, normalizeDateRange } from "@/packages/dashboard/utils/date-range-helpers";
import {
  createPresetRange,
  DEFAULT_RANGE_PRESET,
  RANGE_PRESETS,
  RangePresetValue,
} from "@/packages/dashboard/utils/date-range-presets";
import {
  MantineDateRangeValue,
  normalizeMantineRangeForPicker,
  resolveMantineRangeIsoBounds,
} from "@/packages/dashboard/utils/mantine-date-range";

import { Alert, Center, Group, Loader, Paper, SegmentedControl, Stack, Tabs, Text, useMantineTheme } from "@mantine/core";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { aggregateMeals, type MacroTargetValues } from "./macro-aggregations";
import {
  DailyBreakdownTable,
  MacroSummaryGrid,
  MacroTargetsSummary,
  MealDetailsAccordion,
  WeeklyBreakdownTable,
} from "./MealsSummarySections";

const buildQueryParams = (preset: RangePresetValue, range: MantineDateRangeValue | null) => {
  const [rawStart, rawEnd] = range ?? [];
  if (!rawStart || !rawEnd) {
    return null;
  }

  if (preset === "custom") {
    const { start_timestamp, end_timestamp } = resolveMantineRangeIsoBounds(range);
    if (!start_timestamp || !end_timestamp) {
      return null;
    }

    return {
      start_timestamp,
      end_timestamp,
    };
  }

  const startIso = formatIsoDate(rawStart);
  const endIso = formatIsoDate(rawEnd);

  if (!startIso || !endIso) {
    return null;
  }

  return {
    start_timestamp: startIso,
    end_timestamp: endIso,
  };
};

const rangeLabelFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const formatRangeLabel = (value: MantineDateRangeValue | null) => {
  const { start, end } = normalizeDateRange(value);
  if (!start || !end) {
    return "Select a date range";
  }

  return `${rangeLabelFormatter.format(start)} – ${rangeLabelFormatter.format(end)}`;
};

type LedgerView = "daily" | "weekly";

const buildMacroTargetValues = (targets?: UserTargets | null): MacroTargetValues => ({
  calories: targets?.caloriesDailyTarget ?? null,
  proteins: targets?.proteinsDailyTarget ?? null,
  carbs: targets?.carbsDailyTarget ?? null,
  fats: targets?.fatsDailyTarget ?? null,
});

export const MealsSection = () => {
  const theme = useMantineTheme()
  const [preset, setPreset] = useState<RangePresetValue>(DEFAULT_RANGE_PRESET);
  const [range, setRange] = useState<MantineDateRangeValue>(() => createPresetRange(DEFAULT_RANGE_PRESET));
  const [customRange, setCustomRange] = useState<MantineDateRangeValue>(() => createPresetRange(DEFAULT_RANGE_PRESET));
  const [ledgerView, setLedgerView] = useState<LedgerView>("daily");
  const { data: userTargets, isLoading: isLoadingTargets, error: targetsError } = useUserTargets();

  const queryParams = useMemo(() => buildQueryParams(preset, range), [preset, range]);
  const dailyMacroTargets = useMemo(() => buildMacroTargetValues(userTargets), [userTargets]);
  const targetsQueryError = targetsError instanceof Error ? targetsError : null;

  const {
    data: mealsResponse,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["meals", queryParams?.start_timestamp, queryParams?.end_timestamp],
    queryFn: () => {
      if (!queryParams) {
        throw new Error("Missing date range for meals request");
      }

      return getMeals(queryParams);
    },
    enabled: Boolean(queryParams),
  });

  const meals = mealsResponse?.meals ?? [];
  const aggregates = useMemo(() => aggregateMeals(meals), [meals]);
  const { overallTotals, dayGroups, weekGroups } = aggregates;
  const hasMeals = meals.length > 0;
  const showInitialLoader = isLoading && !mealsResponse;
  const rangeLabel = useMemo(() => formatRangeLabel(range), [range]);
  const headlineStats = useMemo(
    () => [
      { label: "Meals logged", value: String(meals.length) },
      { label: "Days logged", value: String(dayGroups.length) },
      {
        label: "Avg meals/day",
        value: dayGroups.length === 0 ? "0" : (meals.length / dayGroups.length).toFixed(1),
      },
    ],
    [dayGroups.length, meals.length],
  );
  useEffect(() => {
    if (ledgerView === "weekly" && weekGroups.length === 0) {
      setLedgerView("daily");
    }
  }, [ledgerView, weekGroups.length]);

  const handlePresetChange = (value: string) => {
    const nextPreset = value as RangePresetValue;
    setPreset(nextPreset);

    if (nextPreset === "custom") {
      setRange(customRange);
      return;
    }

    const nextRange = createPresetRange(nextPreset);
    setRange(nextRange);
  };

  const handleRangeChange = (value: DatesRangeValue<string> | null) => {
    const nextRange: MantineDateRangeValue = [value?.[0] ?? null, value?.[1] ?? null];
    setCustomRange(nextRange);
    if (preset === "custom") {
      setRange(nextRange);
    }
  };

  const dailyWeeklySummaryView = () => {
    return (
      <Stack gap={12}>
        <Tabs value={ledgerView} onChange={(value) => value && setLedgerView(value as LedgerView)}>
          <Tabs.List>
            <Tabs.Tab value="daily">Daily breakdown</Tabs.Tab>
            <Tabs.Tab value="weekly" disabled={weekGroups.length === 0}>
              Weekly breakdown
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="daily" pt="xs">
            <DailyBreakdownTable dayGroups={dayGroups} macroTargets={dailyMacroTargets} />
          </Tabs.Panel>

          <Tabs.Panel value="weekly" pt="xs">
            {weekGroups.length === 0 ? (
              <Text size="sm" style={{ color: "#4B5563" }}>
                Weekly summaries will appear once at least seven days are logged.
              </Text>
            ) : (
              <WeeklyBreakdownTable weekGroups={weekGroups} macroTargets={dailyMacroTargets} targetMultiplier={7} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    );
  };

  const headerInfoView = (
    <Stack gap={"xl"}>
      <Group justify="space-between" align="flex-start" gap="lg" wrap="wrap">
        <Stack gap={6} mb={"md"}>
          <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.1em", color: "#111827" }}>
            Meals report
          </Text>
          <Group gap="xs" align="center">
            <Text fw={600} size="lg" style={{ color: "#111827" }}>
              {rangeLabel}
            </Text>
            {isFetching && !showInitialLoader && <Loader size="xs" color="var(--mantine-color-blue-6)" />}
          </Group>
        </Stack>
        <MacroSummaryGrid totals={overallTotals} headlineStats={headlineStats} />
      </Group>

      {error && (
        <Alert color="red" title="Unable to load meals">
          {error instanceof Error ? error.message : "An unexpected error occurred."}
        </Alert>
      )}

      {showInitialLoader && !error && (
        <Center mih={200}>
          <Loader />
        </Center>
      )}

      {!showInitialLoader && !error && !hasMeals && (
        <Stack gap={4} align="left" p={"lg"} bg={theme.colors.teal[1]}>
          <Text fw={600} style={{ color: "#111827" }}>
            Nothing logged in this date range.
          </Text>
          <Text size="sm" style={{ color: "#4B5563" }}>
            Try expanding the date range or logging a new meal.
          </Text>
        </Stack>
      )}

      <Stack gap={16}>
        <MacroTargetsSummary
          totals={overallTotals}
          dayCount={dayGroups.length}
          dailyTargets={dailyMacroTargets}
          isLoadingTargets={isLoadingTargets}
          targetsError={targetsQueryError}
        />
      </Stack>

      {dailyWeeklySummaryView()}
    </Stack>
  );

  const customDateRangePickerView = (
    <Stack gap={4}>
      <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
        Custom dates
      </Text>
      <DatePickerInput
        type="range"
        value={normalizeMantineRangeForPicker(customRange)}
        onChange={handleRangeChange}
        disabled={preset !== "custom"}
        aria-disabled={preset !== "custom"}
        hideOutsideDates
        allowSingleDateInRange
        maxDate={new Date()}
        clearable={false}
        placeholder="Select start and end dates"
        size="sm"
        radius="md"
        styles={{
          input: {
            borderColor: "#E5E7EB",
          },
        }}
      />
    </Stack>
  );

  const rangePickerView = (
    <Stack gap={8}>
      <Stack gap={4}>
        <Text size="xs" tt="uppercase" fw={600} style={{ letterSpacing: "0.08em", color: "#111827" }}>
          Date Range
        </Text>
        <SegmentedControl
          orientation={"horizontal"}
          value={preset}
          onChange={handlePresetChange}
          data={RANGE_PRESETS}
          fullWidth
          size="md"
        />
      </Stack>
      {preset === "custom" && customDateRangePickerView}
    </Stack>
  );

  return (
    <Stack gap={"lg"}>
      {rangePickerView}

      <Paper
        withBorder
        radius="md"
        p="xl"
        style={{
          backgroundColor: "#FFFFFF",
          borderColor: "#E5E7EB",
          boxShadow: "0 12px 24px -20px rgba(15, 23, 42, 0.35)",
        }}
      >
        <Stack gap="xl">
          {headerInfoView}
          {!showInitialLoader && !error && hasMeals && (
            <Stack gap="xl">
              <MealDetailsAccordion dayGroups={dayGroups} dailyCalorieTarget={dailyMacroTargets.calories} />
            </Stack>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
